import React, { createContext, useCallback, useState } from "react";

import {
    createSale,
    deleteSale as deleteSaleApi,
    deleteSaleFile,
    fetchSale,
    fetchSaleFile,
    fetchSalesList,
    fetchSalesFinancialStats,
    fetchSalesStats,
    fetchUpcomingSales,
    updateSale as updateSaleApi,
    uploadSalesFile,
} from "../api/salesApi";
import { DEFAULT_SALES_PAGE_SIZE, SERVER_PAGINATION_ENABLED } from "../constants/config";

export const SalesContext = createContext();

const initialPagination = {
    currentPage: 1,
    totalPages: 1,
    totalSales: 0,
    limit: DEFAULT_SALES_PAGE_SIZE,
};

export const Provider = (props) => {
    const [loading, setLoading] = useState(false);
    const [salesList, setSalesList] = useState();
    const [salesPagination, setSalesPagination] = useState(initialPagination);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [filter, setFilter] = useState("All");
    const [search, setSearch] = useState("");
    const [updating, setUpdating] = useState(false);

    const getSalesList = useCallback(async (options = {}) => {
        const {
            page = 1,
            limit = DEFAULT_SALES_PAGE_SIZE,
            status,
            excludeStatuses,
            balance,
            search: searchTerm,
            customerId,
            sortBy,
            sortOrder,
        } = options;

        setLoading(true);
        try {
            const params = SERVER_PAGINATION_ENABLED
                ? {
                      page,
                      limit,
                      status,
                      excludeStatuses,
                      balance,
                      search: searchTerm,
                      customerId,
                      sortBy,
                      sortOrder,
                  }
                : { page: 1, limit: 1000 };

            const data = await fetchSalesList(params);
            if (!data) return;

            // Tolerate older API responses that may return a bare array.
            if (Array.isArray(data)) {
                setSalesList(data);
                setSalesPagination({
                    ...initialPagination,
                    totalSales: data.length,
                });
                return { salesList: data, pagination: initialPagination };
            }

            setSalesList(data.salesList || []);
            setSalesPagination(data.pagination || initialPagination);
            return data;
        } catch (error) {
            console.log("Retrieving Sales List Error: ", error);
        } finally {
            setLoading(false);
        }
    }, []);

    const addSale = async (newSale) => {
        try {
            const created = await createSale(newSale);
            await getSalesList({
                page: salesPagination.currentPage,
                limit: salesPagination.limit,
            });
            return created;
        } catch (error) {
            console.log("Adding Sales Error :", error);
            return error;
        }
    };

    const getSale = async (salesId) => {
        try {
            return await fetchSale(salesId);
        } catch (error) {
            console.log("Retrieving Sale Error: ", error);
        }
    };

    const updateSale = async (saleId, updatedSale) => {
        try {
            setUpdating(true);
            const data = await updateSaleApi(saleId, updatedSale);
            setSalesList((prev) =>
                prev
                    ? prev.map((sale) => (sale._id === saleId ? { ...sale, ...updatedSale } : sale))
                    : prev,
            );
            return data;
        } catch (error) {
            console.log("Updating Sale Error: ", error);
        } finally {
            setUpdating(false);
        }
    };

    const deleteSale = async (saleId) => {
        try {
            await deleteSaleApi(saleId);
            setSalesList((prev) => (prev ? prev.filter((sale) => sale._id !== saleId) : prev));
        } catch (error) {
            console.log("Deleting Sale Error: ", error);
            return error;
        }
    };

    const upload = async (files) => {
        try {
            return await uploadSalesFile(files);
        } catch (error) {
            console.log("Uploading File Error: ", error);
        }
    };

    const getFiles = async (id) => {
        try {
            return await fetchSaleFile(id);
        } catch (error) {
            console.log("Getting Files Error: ", error);
        }
    };

    const deleteFile = async (file) => {
        try {
            return await deleteSaleFile(file);
        } catch (error) {
            console.log("Deleting File Error: ", error);
        }
    };

    const getStats = async () => {
        try {
            return await fetchSalesStats();
        } catch (error) {
            console.log("Retrieving Sales Stats Error: ", error);
        }
    };

    const getFinancialStats = async () => {
        try {
            return await fetchSalesFinancialStats();
        } catch (error) {
            console.log("Retrieving Sales Financial Stats Error: ", error);
        }
    };

    const getUpcoming = async (limit = 5) => {
        try {
            return await fetchUpcomingSales(limit);
        } catch (error) {
            console.log("Retrieving Upcoming Sales Error: ", error);
        }
    };

    // Convert decimal to fractional inches. Used by sale detail rendering.
    const getFraction = (number) => {
        function getGcd(num1, num2) {
            if (num2 === 0) return num1;
            return getGcd(num2, num1 % num2);
        }

        let wholeNumber = Math.floor(number);
        let decimal = number - wholeNumber;
        let decimalLength = decimal.toString().length - 1;
        let divider;

        if (decimalLength === 1) {
            divider = 1;
        } else if (decimalLength === 2) {
            divider = 10;
        } else if (decimalLength === 3) {
            divider = 100;
        } else {
            divider = 1000;
        }

        let num = decimal * divider;
        let gcd = getGcd(num, divider);

        const numerator = num / gcd;
        const denominator = divider / gcd;

        if (numerator === 0) return `${wholeNumber}`;
        return `${wholeNumber}-${numerator}/${denominator}`;
    };

    return (
        <SalesContext.Provider
            value={{
                loading,
                filter,
                salesList,
                salesPagination,
                search,
                scrollPosition,
                updating,
                action: {
                    addSale,
                    deleteFile,
                    deleteSale,
                    getFiles,
                    getFraction,
                    getSale,
                    getSalesList,
                    getFinancialStats,
                    getStats,
                    getUpcoming,
                    setFilter,
                    setLoading,
                    setSalesList,
                    setScrollPosition,
                    setSearch,
                    setUpdating,
                    updateSale,
                    upload,
                },
            }}
        >
            {props.children}
        </SalesContext.Provider>
    );
};
